import { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { type ExternalToast, Toaster as Sonner, toast, useSonner } from "sonner";
import { useReadLocalStorage } from "usehooks-ts";
import { TOASTER_POSITION_KEY } from "@remi/constants";
import type { RemiErrorDeprecated } from "@remi/error";
import { RnCheckmark, RnInfo, RnMessage, RnScheduleMessage, RnX } from "@remi/icons/next";
import { RemiSpinner } from "@remi/next-ui";
import { useBreakpoint } from "@remi/next-ui/hooks";
import { ProjectEvaluationToastType } from "@remi/types/project-evalutation";

import { parseToastPosition } from "~/utils/toast-position";
import { TOASTER_BEHIND_DIALOG_Z_INDEX, useIsModalOverlayOpen } from "~/hooks/useOverlayState";
import { cn } from "~/lib/utils";

type Variant = "success" | "error" | "warning" | "info" | "secondary";

interface DefaultToastProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  variant?: Variant;
  link?: React.JSX.Element;
  error?: string | RemiErrorDeprecated;
  toastId?: string | number;
  preserveCase?: boolean;
}

type ToasterProps = React.ComponentProps<typeof Sonner>;

// A new simplified way to display toast notifications
type DisplayToastProps = Omit<ExternalToast, "title" | "description"> & Omit<DefaultToastProps, "title" | "variant">;

export const display = {
  success: (title: string, data?: DisplayToastProps) => displayToast({ title, variant: "success", ...data }),
  info: (title: string, data?: DisplayToastProps) => displayToast({ title, variant: "info", ...data }),
  warning: (title: string, data?: DisplayToastProps) => displayToast({ title, variant: "warning", ...data }),
  error: (title: string, data?: DisplayToastProps) => displayToast({ title, variant: "error", ...data }),
  loading: (title: string, data?: DisplayToastProps) =>
    displayToast({ title, variant: "info", icon: <RemiSpinner />, ...data }),
  dismiss: (toastId: string | number) => toast.dismiss(toastId),
};

/**
 * Display a toast notification
 *
 * @param title - The title of the toast
 * @param description - The description of the toast
 * @param icon - The icon to display in the toast
 * @param variant - The variant of the toast (success, error, warning, info)
 * @param link - A link to display in the toast
 */
export const displayToast = ({
  title,
  description,
  icon,
  variant,
  link,
  preserveCase,
  ...props
}: DefaultToastProps & ExternalToast) =>
  toast.custom((toastId) => <DefaultToast {...{ title, description, icon, variant, link, toastId, preserveCase }} />, {
    ...props,
  });

interface PendingToastProps extends ExternalToast {
  promise: Promise<unknown>;
  loading?: string;
  success?: string;
  error?: string | ((reason: unknown) => string);
}

export const displayPendingToast = ({ loading, success, error, promise, ...props }: PendingToastProps) => {
  const toastId = toast.custom(
    (id) => (
      <DefaultToast
        title={loading || "Loading..."}
        icon={
          <span className="inline-flex size-4 items-center justify-center">
            <RemiSpinner size="sm" />
          </span>
        }
        variant="info"
        toastId={id}
      />
    ),
    {
      ...props,
    },
  );

  void promise
    .then(() => {
      toast.custom(
        (id) => (
          <DefaultToast
            title={success || "Success!"}
            variant="success"
            icon={<RnCheckmark size="sm" className="text-icon-success" />}
            toastId={id}
          />
        ),
        {
          ...props,
          id: toastId,
        },
      );
    })
    .catch((reason: unknown) => {
      toast.custom(
        (id) => (
          <DefaultToast
            title={(typeof error === "function" ? error(reason) : error) || "Error!"}
            variant="error"
            icon={<RnX size="sm" className="text-icon-destructive" />}
            toastId={id}
          />
        ),
        {
          ...props,
          id: toastId,
        },
      );
    });
};

const Toaster = ({ ...props }: ToasterProps) => {
  const isMd = useBreakpoint("isMd");
  const isModalOverlayOpen = useIsModalOverlayOpen();
  const defaultPosition: ToasterProps["position"] = isMd ? "bottom-right" : "bottom-center";

  const position =
    useReadLocalStorage<ToasterProps["position"]>(TOASTER_POSITION_KEY, {
      deserializer: parseToastPosition,
    }) ?? defaultPosition;

  return (
    <>
      <Sonner
        duration={10000}
        visibleToasts={4}
        position={position}
        // The Toaster sits outside the page tree, so a page with a floating bottom bar sets --toaster-bottom-offset
        // on <html> (and removes it on unmount) to lift bottom toasts clear of it. The fallbacks are Sonner's defaults.
        offset={{ bottom: "var(--toaster-bottom-offset, 24px)" }}
        mobileOffset={{ bottom: "var(--toaster-bottom-offset, 16px)" }}
        // Sonner anchors bottom-right toasts to the browser viewport edge. The app's
        // bottom nav / sidebar occupies ~24px horizontal and ~30px vertical space at
        // that corner, so we nudge the container inward to avoid overlap. Applying this
        // to the container (not per-toast) prevents the offset from compounding when
        // Sonner fans out the stack on hover. Other positions are far from the nav.
        // A modal vaul drawer sets `pointer-events: none` on document.body; the toaster is
        // portaled there and inherits it, so without this toasts become unclickable.
        className={cn(
          "system-toaster pointer-events-auto",
          position === "bottom-right" && "-translate-x-[24px] -translate-y-[30px]",
        )}
        style={isModalOverlayOpen ? { zIndex: TOASTER_BEHIND_DIALOG_Z_INDEX } : undefined}
        icons={{
          close: <RnX size="sm" />,
        }}
        toastOptions={{
          unstyled: true,
          classNames: {
            default: cn(
              "w-[350px] rounded-md border border-border-primary bg-background-primary p-2 text-[13px] shadow-xl",
            ),
            toast: "bg-background text-foreground border-border shadow-lg",
            description: "text-text-secondary",
            actionButton: "bg-primary text-primary-foreground focus-visible:outline-hidden focus-visible:ring-2",
            cancelButton: "bg-muted text-text-secondary focus-visible:outline-hidden focus-visible:ring-2",
            closeButton:
              "rounded-md bg-background-primary text-text-primary transition-colors hover:bg-background-primaryHover focus-visible:outline-hidden focus-visible:ring-2 p-1 flex items-start h-min-content max-h-6",
            loader: "hidden",
          },
        }}
        {...props}
      />
      <SystemToastClearAll />
    </>
  );
};

const variantIconColors: Record<Variant, string> = {
  success: "text-icon-success",
  error: "text-icon-destructive",
  warning: "text-icon-yellow",
  info: "text-icon-secondary",
  secondary: "text-icon-secondary",
};

const dismissSystemToasts = () => {
  for (const t of toast.getToasts()) {
    if (!("dismiss" in t) && t.toasterId !== "notifications") {
      toast.dismiss(t.id);
    }
  }
};

const CLEAR_ALL_GAP_PX = 8;

const SystemToastClearAll = () => {
  const { toasts } = useSonner();
  const isModalOverlayOpen = useIsModalOverlayOpen();
  const systemCount = toasts.filter((t) => t.toasterId !== "notifications").length;
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sonner constantly re-transforms its <li>s (enter, hover fan-out, exit) without resizing
  // the <ol>, so observers go stale mid-animation; re-measuring each frame keeps the pinned
  // button glued to the visual top of the stack in every state.
  useLayoutEffect(() => {
    if (systemCount < 2) {
      return;
    }
    let frame = requestAnimationFrame(function follow() {
      const button = buttonRef.current;
      const list = document.querySelector<HTMLElement>("[data-sonner-toaster].system-toaster");
      const items = list?.querySelectorAll<HTMLElement>(':scope > li:not([data-visible="false"])');
      if (button && items && items.length > 0) {
        let stackTop = Number.POSITIVE_INFINITY;
        let stackRight = window.innerWidth;
        for (const item of items) {
          const rect = item.getBoundingClientRect();
          stackTop = Math.min(stackTop, rect.top);
          stackRight = Math.min(stackRight, window.innerWidth - rect.right);
        }
        button.style.top = `${stackTop - button.offsetHeight - CLEAR_ALL_GAP_PX}px`;
        button.style.right = `${stackRight}px`;
        button.style.visibility = "visible";
      }
      frame = requestAnimationFrame(follow);
    });
    return () => cancelAnimationFrame(frame);
  }, [systemCount]);

  if (systemCount < 2) {
    return null;
  }

  return createPortal(
    <button
      ref={buttonRef}
      type="button"
      aria-label="Clear all toasts"
      // Radix only hides outside content from the tab order when the dialog is `modal`.
      tabIndex={isModalOverlayOpen ? -1 : undefined}
      aria-hidden={isModalOverlayOpen || undefined}
      onClick={dismissSystemToasts}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        visibility: "hidden",
        zIndex: isModalOverlayOpen ? TOASTER_BEHIND_DIALOG_Z_INDEX : 9999,
      }}
      className="flex items-center rounded-full border border-border-primary bg-background-primary px-2 py-0.5 text-text-secondary text-xxs shadow-modal-md transition-colors hover:bg-background-primaryHover max-sm:min-h-11 max-sm:px-4 max-sm:text-sm"
    >
      Clear all
    </button>,
    document.body,
  );
};

const DefaultToast = ({
  title,
  description,
  icon,
  variant = "info",
  link,
  toastId,
  preserveCase = false,
}: DefaultToastProps) => {
  let autoIcon: React.ReactNode;
  switch (variant) {
    case "success":
      autoIcon = <RnCheckmark size={"sm"} className={cn(variantIconColors[variant])} />;
      break;
    case "error":
      autoIcon = <RnX size={"sm"} className={cn(variantIconColors[variant])} />;
      break;
    case "warning":
    case "info":
    case "secondary":
      autoIcon = <RnInfo size={"sm"} className={cn(variantIconColors[variant])} />;
  }
  return (
    <div className={cn("flex gap-2 py-1 pl-1", description || link ? "items-start" : "items-center")}>
      <div className={cn("flex size-4 shrink-0 items-center justify-center", variantIconColors[variant])}>
        {icon ?? autoIcon}
      </div>
      <div className="flex flex-1 flex-col gap-1 text-xs">
        <div className={cn("font-semibold text-text-primary", !preserveCase && "lowercase first-letter:uppercase")}>
          {title}
        </div>
        {description && (
          <div className={cn("text-text-secondary", !preserveCase && "first-letter:uppercase")}>{description}</div>
        )}
        {link}
      </div>
      {toastId !== undefined && (
        <button
          type="button"
          aria-label="Close toast"
          onClick={() => toast.dismiss(toastId)}
          className="shrink-0 rounded-md bg-background-primary p-1 text-text-primary transition-colors hover:bg-background-primaryHover focus-visible:outline-hidden focus-visible:ring-2 max-sm:-m-2.5 max-sm:p-3.5"
        >
          <RnX size="sm" />
        </button>
      )}
    </div>
  );
};

export const ProjectEvaluationToastIcon: Record<ProjectEvaluationToastType, React.ReactNode> = {
  [ProjectEvaluationToastType.MESSAGE]: <RnMessage className="text-icon-secondary" />,
  [ProjectEvaluationToastType.SCHEDULED_MESSAGE]: <RnScheduleMessage className="text-icon-secondary" />,
};

export { Toaster };
